/**
 * Hand Pose Library — save, load, and manage user-defined hand poses.
 *
 * Follows the same cloud-library pattern as poseLibrary.ts but stores
 * per-bone delta quaternions (relative to rest) for finger bones only.
 * Deltas are model-agnostic: they compose correctly with any mannequin
 * variant's rest pose.
 */

import { getRestPose } from "../mannequin/boneRegistry.ts";
import {
  mirrorQuaternion,
  type Quat4,
  qinv,
  qmul,
} from "../mannequin/quaternionMath.ts";
import { applyHandPose, type HandPose } from "./defaultHandPoses.ts";
import { useLibraryStore } from "./libraryStore.ts";
import { uploadAndUpdateItem, uploadLibraryItem } from "./poseLibrary.ts";
import { useStore } from "./store.ts";

// ---------------------------------------------------------------------------
// File format
// ---------------------------------------------------------------------------

export interface HandPoseFile {
  magic: "POSEREF_HAND_POSE_V1";
  /**
   * Per-bone delta quaternions for the LEFT hand.
   * Key = short name ("thumb1" ... "pinky3"), value = [x, y, z, w].
   *
   * At load time, right-hand deltas are derived by mirroring (negate Y, Z).
   */
  deltas: Record<string, Quat4>;
  /** Number of finger bones in the pose. */
  boneCount: number;
}

// ---------------------------------------------------------------------------
// Finger bone helpers
// ---------------------------------------------------------------------------

const FINGERS = ["thumb", "index", "middle", "ring", "pinky"] as const;

/**
 * Capture left-hand delta quaternions from the current pose.
 *
 * For each finger bone, computes:
 *   delta = inv(rest) * posed
 *
 * If both hands are posed, uses the left hand.  If only the right hand is
 * posed, mirrors its deltas to produce left-hand equivalents.
 */
function captureHandDeltas(objectId: string): Record<string, Quat4> | null {
  const state = useStore.getState();
  const bonePoses = state.mannequinBonePoses.get(objectId);
  if (!bonePoses) return null;

  // Determine which side has data
  const hasLeft = FINGERS.some((f) => bonePoses.has(`finger_${f}L1`));
  const hasRight = FINGERS.some((f) => bonePoses.has(`finger_${f}R1`));
  if (!hasLeft && !hasRight) return null;

  const side = hasLeft ? "L" : "R";
  const deltas: Record<string, Quat4> = {};
  let count = 0;

  for (const finger of FINGERS) {
    for (let j = 1; j <= 3; j++) {
      const boneName = `finger_${finger}${side}${j}`;
      const posed = bonePoses.get(boneName);
      const rest = getRestPose(objectId, boneName);
      if (!posed || !rest) continue;

      // delta = inv(rest) * posed  (local-frame delta)
      let delta = qmul(qinv(rest), posed);

      // If we captured from the right hand, mirror to left-hand convention
      if (side === "R") delta = mirrorQuaternion(delta);

      const key = `${finger}${j}`;
      deltas[key] = delta;
      count++;
    }
  }

  return count > 0 ? deltas : null;
}

function buildHandPoseFile(deltas: Record<string, Quat4>): HandPoseFile {
  return {
    magic: "POSEREF_HAND_POSE_V1",
    deltas,
    boneCount: Object.keys(deltas).length,
  };
}

// ---------------------------------------------------------------------------
// Save to cloud library
// ---------------------------------------------------------------------------

export async function saveHandPoseToLibrary(
  objectId: string,
  name: string,
): Promise<boolean> {
  const deltas = captureHandDeltas(objectId);
  if (!deltas) return false;

  const data = buildHandPoseFile(deltas);
  return uploadLibraryItem("hand_pose", name, data, {
    objectCount: data.boneCount,
  });
}

// ---------------------------------------------------------------------------
// Load from cloud library
// ---------------------------------------------------------------------------

/**
 * Load a hand pose file and apply it to the specified hand(s).
 */
export async function loadHandPoseFromLibrary(
  fileUrl: string,
  objectId: string,
  sides: Set<"L" | "R">,
): Promise<boolean> {
  try {
    const res = await fetch(fileUrl);
    const data = (await res.json()) as HandPoseFile;
    if (data.magic !== "POSEREF_HAND_POSE_V1") return false;

    const pose: HandPose = {
      id: "library:loaded",
      name: "",
      deltas: data.deltas as Record<string, Quat4>,
    };

    applyHandPose(pose, objectId, sides);
    return true;
  } catch (e) {
    console.error("Failed to load hand pose:", e);
    return false;
  }
}

// ---------------------------------------------------------------------------
// Overwrite existing library item with current state
// ---------------------------------------------------------------------------

export async function overwriteHandPoseInLibrary(
  itemId: string,
  objectId: string,
): Promise<boolean> {
  const deltas = captureHandDeltas(objectId);
  if (!deltas) return false;

  const data = buildHandPoseFile(deltas);
  const ok = await uploadAndUpdateItem(itemId, "hand_pose", data, {
    objectCount: data.boneCount,
  });
  if (ok) await useLibraryStore.getState().fetchItems();
  return ok;
}
